"use client";

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { trackEvent } from '@/lib/tracking-utils';

interface CartItem {
  id: string;
  productId: string;
  quantity: number;
  product: {
    id: string;
    name: string;
    price: number;
    thumbnail?: string;
    sku: string;
    inStock: boolean;
    slug?: string;
  };
}

interface Cart {
  id: string;
  sessionId: string;
  items: CartItem[];
  totalItems: number;
  totalPrice: number;
}

interface CartContextType {
  cart: Cart | null;
  loading: boolean;
  addToCart: (product: any, quantity?: number) => Promise<void>;
  updateQuantity: (itemId: string, quantity: number) => Promise<void>;
  removeFromCart: (itemId: string) => Promise<void>;
  clearCart: () => Promise<void>;
  getTotalItems: () => number;
  getTotalPrice: () => number;
  isInCart: (productId: string) => boolean;
  getItemQuantity: (productId: string) => number;
  refreshCart: () => Promise<void>;
}

const CartContext = createContext<CartContextType | null>(null);

interface CartProviderProps {
  storeSlug: string;
  children: React.ReactNode;
}

export function CartProvider({ storeSlug, children }: CartProviderProps) {
  const [cart, setCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);

  // Get session ID from localStorage or create new one
  const getSessionId = useCallback(() => {
    if (typeof window === 'undefined') return null;
    
    let sid = localStorage.getItem(`cart_session_${storeSlug}`);
    if (!sid) {
      sid = crypto.randomUUID();
      localStorage.setItem(`cart_session_${storeSlug}`, sid);
    }
    return sid;
  }, [storeSlug]);

  // Load cart from server
  const refreshCart = useCallback(async () => {
    if (!storeSlug) return;

    setLoading(true);
    try {
      const sid = getSessionId();
      if (!sid) return;

      const response = await fetch(`/api/store-front/cart?storeSlug=${storeSlug}&sessionId=${sid}`);
      const data = await response.json();

      if (data.success) {
        setCart(data.cart);
        setSessionId(data.cart.sessionId);
        // Update localStorage with the session ID from server
        localStorage.setItem(`cart_session_${storeSlug}`, data.cart.sessionId);
        localStorage.setItem(`cart_last_update_${storeSlug}`, Date.now().toString());
      }
    } catch (error) {
      console.error('Error loading cart:', error);
    } finally {
      setLoading(false);
    }
  }, [storeSlug, getSessionId]);

  // Add item to cart
  const addToCart = useCallback(async (product: any, quantity: number = 1) => {
    if (!storeSlug) return;

    setLoading(true);
    
    // Optimistic update - immediately update cart count
    const optimisticCart = cart ? {
      ...cart,
      items: [...cart.items, {
        id: `temp-${Date.now()}`,
        productId: product.id,
        quantity,
        product: {
          id: product.id,
          name: product.name,
          price: product.price,
          thumbnail: product.thumbnail,
          sku: product.sku || '',
          inStock: product.inStock,
          slug: product.slug,
        }
      }],
      totalItems: (cart.totalItems || 0) + quantity,
      totalPrice: (cart.totalPrice || 0) + (product.price * quantity)
    } : null;
    
    if (optimisticCart) {
      setCart(optimisticCart);
    }

    try {
      const sid = getSessionId();
      if (!sid) return;

      const response = await fetch('/api/store-front/cart', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          storeSlug,
          productId: product.id,
          quantity,
          sessionId: sid,
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Update cart with server response
        const updatedCart = {
          id: cart?.id || '',
          sessionId: cart?.sessionId || '',
          items: cart?.items.map(item => 
            item.id === `temp-${Date.now()}` ? data.item : item
          ).filter(item => item.id !== `temp-${Date.now()}`) || [],
          totalItems: data.cart?.totalItems || (cart?.totalItems || 0) + quantity,
          totalPrice: data.cart?.totalPrice || (cart?.totalPrice || 0) + (product.price * quantity)
        };
        setCart(updatedCart);
        
        toast.success(data.message || 'Product added to cart');
        
        // Track add to cart event
        trackEvent.addToCart(product, quantity);
      } else {
        // Revert optimistic update on error
        if (cart) {
          setCart(cart);
        }
        toast.error(data.error || 'Failed to add product to cart');
      }
    } catch (error) {
      // Revert optimistic update on error
      if (cart) {
        setCart(cart);
      }
      console.error('Error adding to cart:', error);
      toast.error('Failed to add product to cart');
    } finally {
      setLoading(false);
    }
  }, [storeSlug, getSessionId, cart]);

  // Update item quantity
  const updateQuantity = useCallback(async (itemId: string, quantity: number) => {
    if (!storeSlug) return;

    setLoading(true);
    
    // Optimistic update
    const itemToUpdate = cart?.items.find(item => item.id === itemId);
    const optimisticCart = cart && itemToUpdate ? {
      ...cart,
      items: cart.items.map(item => 
        item.id === itemId ? { ...item, quantity } : item
      ),
      totalItems: cart.items.reduce((sum, item) => 
        sum + (item.id === itemId ? quantity : item.quantity), 0
      ),
      totalPrice: cart.items.reduce((sum, item) => 
        sum + (item.id === itemId ? item.product.price * quantity : item.product.price * item.quantity), 0
      )
    } : cart;
    
    if (optimisticCart) {
      setCart(optimisticCart);
    }

    try {
      const sid = getSessionId();
      if (!sid) return;

      const response = await fetch('/api/store-front/cart', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          itemId,
          quantity,
          sessionId: sid,
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Update cart with server response
        const updatedCart = {
          id: cart?.id || '',
          sessionId: cart?.sessionId || '',
          items: cart?.items.map(item => 
            item.id === itemId ? data.item : item
          ) || [],
          totalItems: data.cart?.totalItems || optimisticCart?.totalItems || 0,
          totalPrice: data.cart?.totalPrice || optimisticCart?.totalPrice || 0
        };
        setCart(updatedCart);
        
        toast.success(data.message || 'Cart updated');
      } else {
        // Revert optimistic update on error
        if (cart) {
          setCart(cart);
        }
        toast.error(data.error || 'Failed to update cart');
      }
    } catch (error) {
      // Revert optimistic update on error
      if (cart) {
        setCart(cart);
      }
      console.error('Error updating cart:', error);
      toast.error('Failed to update cart');
    } finally {
      setLoading(false);
    }
  }, [storeSlug, getSessionId, cart]);

  // Remove item from cart
  const removeFromCart = useCallback(async (itemId: string) => {
    if (!storeSlug) return;

    setLoading(true);
    
    // Optimistic update - immediately remove item from cart
    const itemToRemove = cart?.items.find(item => item.id === itemId);
    const optimisticCart = cart && itemToRemove ? {
      ...cart,
      items: cart.items.filter(item => item.id !== itemId),
      totalItems: Math.max(0, (cart.totalItems || 0) - itemToRemove.quantity),
      totalPrice: Math.max(0, (cart.totalPrice || 0) - (itemToRemove.product.price * itemToRemove.quantity))
    } : cart;
    
    if (optimisticCart) {
      setCart(optimisticCart);
    }

    try {
      const response = await fetch(`/api/store-front/cart?itemId=${itemId}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (data.success) {
        toast.success(data.message || 'Product removed from cart');
      } else {
        // Revert optimistic update on error
        if (cart) {
          setCart(cart);
        }
        toast.error(data.error || 'Failed to remove product from cart');
      }
    } catch (error) {
      // Revert optimistic update on error
      if (cart) {
        setCart(cart);
      }
      console.error('Error removing from cart:', error);
      toast.error('Failed to remove product from cart');
    } finally {
      setLoading(false);
    }
  }, [storeSlug, cart]);

  // Clear cart
  const clearCart = useCallback(async () => {
    if (!cart?.items || !cart.id) return;

    setLoading(true);
    try {
      // Clear all items at once using the new API endpoint
      const response = await fetch(`/api/store-front/cart?clearAll=true&cartId=${cart.id}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (data.success) {
        // Clear the cart state
        setCart({
          ...cart,
          items: [],
          totalItems: 0,
          totalPrice: 0
        });
        toast.success(data.message || 'Cart cleared successfully');
      } else {
        toast.error(data.error || 'Failed to clear cart');
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
      toast.error('Failed to clear cart');
    } finally {
      setLoading(false);
    }
  }, [cart]);

  // Get total items count
  const getTotalItems = useCallback(() => {
    if (!cart) return 0;
    
    // Use server-provided totalItems if available, otherwise calculate from items
    if (cart.totalItems !== undefined) {
      return cart.totalItems;
    }
    
    // Fallback: calculate from items array
    return cart.items.reduce((sum, item) => sum + item.quantity, 0);
  }, [cart]);

  // Get total price
  const getTotalPrice = useCallback(() => {
    if (!cart) return 0;
    
    // Use server-provided totalPrice if available, otherwise calculate from items
    if (cart.totalPrice !== undefined) {
      return cart.totalPrice;
    }
    
    // Fallback: calculate from items array
    return cart.items.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);
  }, [cart]);

  // Check if product is in cart
  const isInCart = useCallback((productId: string) => {
    return cart?.items.some(item => item.productId === productId) || false;
  }, [cart]);

  // Get item quantity
  const getItemQuantity = useCallback((productId: string) => {
    const item = cart?.items.find(item => item.productId === productId);
    return item?.quantity || 0;
  }, [cart]);

  // Load cart on mount
  useEffect(() => {
    if (storeSlug) {
      refreshCart();
    }
  }, [storeSlug, refreshCart]);

  const contextValue: CartContextType = {
    cart,
    loading,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    getTotalItems,
    getTotalPrice,
    isInCart,
    getItemQuantity,
    refreshCart,
  };

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
}

// Hook to use cart context
export function useCart() {
  const context = useContext(CartContext);
  if (context === null) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
} 